import { useEffect, useState } from "react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import {
  CurrencySelector,
  MarketStatus,
  MetalMarketTable,
  MetalOverviewCard,
  MetalPerformanceComparison,
  MetalPriceChart,
  MetalValueCalculator,
} from "@/features/metals/MetalDashboardParts";
import { useMetalMarket } from "@/features/metals/useMetalMarket";
import type { CurrencyCode, MetalSymbol } from "@/features/metals/types";
export default function MetalDashboard() {
  const { data, loading, refreshing, error, refresh } = useMetalMarket();
  const [currency, setCurrency] = useState<CurrencyCode>(
    () =>
      (localStorage.getItem("cashfux_market_currency") as CurrencyCode) ||
      "EUR",
  );
  const [symbol, setSymbol] = useState<MetalSymbol>("XAU");
  useEffect(() => {
    document.title = "Metallkurse · CashFUX";
  }, []);
  useEffect(() => {
    localStorage.setItem("cashfux_market_currency", currency);
  }, [currency]);
  return (
    <DashboardShell
      title="Metallkurse"
      subtitle="Professionelle Marktübersicht mit echten MetalpriceAPI-Daten"
    >
      <div className="market-dashboard text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Marktübersicht</h1>
            <p className="mt-1 text-sm text-white/40">
              Edelmetallpreise, Entwicklung und Wertberechnung
            </p>
          </div>
          {data && (
            <div className="flex flex-col items-start gap-3 sm:items-end">
              <CurrencySelector value={currency} onChange={setCurrency} />
              <MarketStatus
                data={data}
                refreshing={refreshing}
                refresh={() => void refresh()}
              />
            </div>
          )}
        </div>
        {loading && (
          <div className="mt-8 grid animate-pulse gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-40 border border-white/10 bg-[#07080d]"
              />
            ))}
          </div>
        )}
        {error && (
          <div className="mt-8 border-l-4 border-[#e01e37] bg-[#07080d] p-5">
            <p className="font-semibold">Marktdaten nicht verfügbar</p>
            <p className="mt-1 text-sm text-white/40">{error}</p>
            <button
              onClick={() => void refresh()}
              className="mt-4 h-10 bg-[#e01e37] px-4 text-sm font-semibold text-white"
            >
              Erneut versuchen
            </button>
          </div>
        )}
        {data && (
          <div className="mt-8 space-y-6">
            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {data.latest.rates.map((rate) => (
                <MetalOverviewCard
                  key={rate.symbol}
                  rate={rate}
                  data={data}
                  currency={currency}
                />
              ))}
            </section>
            {!data.latest.rates.length && (
              <div className="border border-dashed border-white/10 bg-[#07080d] p-10 text-center text-white/40">
                Keine Metallkurse verfügbar.
              </div>
            )}
            <MetalPriceChart
              data={data}
              currency={currency}
              symbol={symbol}
              onSymbol={setSymbol}
            />
            <MetalPerformanceComparison data={data} />
            <MetalValueCalculator data={data} currency={currency} />
            <MetalMarketTable data={data} currency={currency} />
            {!data.history && (
              <p className="border-l-2 border-[#e01e37] bg-[#07080d] p-4 text-sm text-white/40">
                Historische Daten sind derzeit nicht verfügbar. Livepreise,
                Tabelle und Rechner funktionieren weiterhin.
              </p>
            )}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
